/**
 * OpenRouter with the user's own key: straight from the browser to
 * openrouter.ai, which answers CORS for any origin. Genie is not involved.
 */

import { ShaderGenerationError } from '../types'
import { getJson, postChatCompletion } from './openai-compatible'
import type { ChatTransportRequest, ShaderModelOption } from './openai-compatible'

export const OPENROUTER_API_URL = 'https://openrouter.ai/api/v1'

/** OpenRouter's app attribution headers, the same ones api-ai sends. */
const ATTRIBUTION = { 'HTTP-Referer': 'https://genie.art', 'X-Title': 'Genie' }

export async function completeOpenRouter(key: string | undefined, request: ChatTransportRequest): Promise<string> {
  if (!key) throw new ShaderGenerationError('Add your OpenRouter API key, or switch billing to Genie credits.', 'AUTH')
  return postChatCompletion(
    {
      url: `${OPENROUTER_API_URL}/chat/completions`,
      label: 'OpenRouter',
      headers: { Authorization: `Bearer ${key}`, ...ATTRIBUTION },
      secret: key,
      onNetworkError: () => new ShaderGenerationError('Could not reach OpenRouter. Check your connection and try again.', 'NETWORK'),
    },
    request,
  )
}

interface OpenRouterModelsResponse {
  data?: Array<{
    id?: string
    name?: string
    architecture?: { output_modalities?: string[] }
  }>
}

/** Every OpenRouter model that answers in text, from the public (keyless) catalogue. */
export async function listOpenRouterModels(signal?: AbortSignal): Promise<ShaderModelOption[]> {
  const body = await getJson<OpenRouterModelsResponse>({ url: `${OPENROUTER_API_URL}/models`, label: 'OpenRouter' }, signal)
  return (body.data ?? [])
    .filter((model) => {
      const outputs = model.architecture?.output_modalities
      return typeof model.id === 'string' && (!outputs || outputs.includes('text'))
    })
    .map((model) => ({ id: model.id as string, label: model.name || (model.id as string) }))
}
